import { generateSlideTypePrompt, createSlideUpdatePrompt as newCreateSlideUpdatePrompt } from './slidePrompts';

export interface SlideGenerationConfig {
  language: 'ko' | 'en';
  slideType: 'card-news' | 'ppt' | 'image-card';
  slideCount: number;
}

export const getLanguageInstruction = (language: 'ko' | 'en'): string => {
  return language === 'ko' 
    ? '한국어로 응답해주세요.' 
    : 'Please respond in English.';
};

/**
 * @deprecated 
 * 이 함수는 더 이상 사용되지 않습니다. 
 * generateSlideTypePrompt를 사용해주세요.
 * 슬라이드 타입별 프롬프트는 slidePrompts.ts에서 관리됩니다.
 */
export const getSlideTypeInstruction = (slideType: 'cardnews' | 'ppt' | 'imagecard', language: 'ko' | 'en'): string => {
  console.warn('getSlideTypeInstruction is deprecated. Use generateSlideTypePrompt from slidePrompts.ts');
  
  switch (slideType) {
    case 'cardnews':
      return language === 'ko' 
        ? '카드뉴스 형태로 만들어주세요. 각 슬라이드는 간결하고 임팩트 있는 메시지로 구성하세요.'
        : 'Create in card news format. Each slide should have concise and impactful messages.';
    case 'imagecard':
      return language === 'ko'
        ? '이미지카드 형태로 만들어주세요. 각 슬라이드에는 관련 이미지가 배치되고, 그 이미지에 알맞는 매우 간결한 메시지를 표시하세요.'
        : 'Create in image card format. Each slide should have a relevant image with very concise messages.';
    case 'ppt':
    default:
      return language === 'ko'
        ? '일반적인 PPT 프레젠테이션 형태로 만들어주세요. 각 슬라이드는 상세하고 풍부한 내용으로 구성하세요.'
        : 'Create in standard PPT presentation format with detailed and rich content.';
  }
};

export const createSlideGenerationPrompt = (
  content: string,
  config: SlideGenerationConfig
): string => {
  const { language, slideType, slideCount } = config;
  
  // 타입별 객체 개수 및 글자 수 규칙
  const getTypeRules = (type: string) => {
    if (type === 'ppt') {
      return {
        objectCount: "2-6개",
        textLength: {
          "2개": "120자 내외 (목록 형태)",
          "4개": "60자 내외", 
          "6개": "30자 내외"
        }
      };
    } else if (type === 'card-news') {
      return {
        objectCount: "1-4개",
        textLength: {
          "1개": "120자 내외",
          "2개": "60자 내외",
          "4개": "30자 내외"
        }
      };
    }
    return { objectCount: "2-4개", textLength: { "기본": "60자 내외" } };
  };

  const typeRules = getTypeRules(slideType);
  
  return `
${getLanguageInstruction(language)}

다음 내용을 바탕으로 정확히 ${slideCount}개의 프레젠테이션 슬라이드를 JSON 형식으로 생성해주세요.

=== 공통 규칙 ===
모든 슬라이드는 제목 객체와 내용 객체로 구분됩니다.

**제목 객체:**
- 화면 상단 15% 높이 차지
- 가로폭 최대 80%, 세로폭(상단 15% 내에서) 최대 80%
- 글씨 크기는 반응형으로 가로폭과 세로폭을 고려하여 결정

**내용 객체:**
- 텍스트, 이미지, 차트를 모두 활용
- 객체 개수: ${typeRules.objectCount}
- 글자 수 규칙: ${Object.entries(typeRules.textLength).map(([key, value]) => `${key} 생성 시: ${value}`).join(', ')}

=== 새로운 JSON 구조 ===
각 슬라이드는 다음의 JSON 객체 배열로 구성됩니다:

{
  "slideNumber": 1-${slideCount}, // 슬라이드 번호
  "category": "title" | "content", // 제목 또는 내용
  "format": "short-text" | "text-list" | "image" | "chart", // 형식
  "designStyle": "title-signboard" | "content-list", // 디자인 스타일
  "content": {
    // 텍스트 객체의 경우
    "items": [
      {
        "designElement": "네모 div" | "원형 div" | "카드 형태" | "배너 형태", // 디자인 요소
        "emoji": "⭐", // 머릿말 이모지
        "heading": "스마트폰", // 머릿말 (굵고 강조)
        "description": "스마트폰은 여러 컴퓨팅 기능을 하는 장치..." // 머릿말 설명 (일반 텍스트)
      }
    ]
    // 또는 이미지/차트의 경우
    "type": "image" | "chart",
    "data": { /* 차트 데이터 */ }
  },
  "metadata": {
    "backgroundColor": "theme", // 테마 색상 사용
    "columns": 1-3 // 칼럼 수
  }
}

=== 디자인 스타일 가이드 ===

1. **제목 (category: "title")**
   - format: "short-text"
   - designStyle: "title-signboard"
   - content: "슬라이드 제목 텍스트"
   - 간결하고 임팩트 있는 제목 (5-15자)

2. **내용 (category: "content")**
   
   A. 텍스트 목록 (format: "text-list")
      - designStyle: "content-list"
      - content.items: 배열 형태로 여러 항목 포함
      - 각 항목은 designElement, emoji, heading, description 구조
      
      **디자인 요소 종류:**
      - "네모 div": 사각형 박스 형태
      - "원형 div": 원형 박스 형태  
      - "카드 형태": 카드 스타일 박스
      - "배너 형태": 가로형 배너 스타일
      
      **구조 예시:**
      "content": {
        "items": [
          {
            "designElement": "네모 div",
            "emoji": "⭐", 
            "heading": "스마트폰",
            "description": "스마트폰은 여러 컴퓨팅 기능을 하는 장치와 앱을 통해 구동하는 소프트웨어로 작동한다"
          }
        ]
      }
   
   B. 이미지 (format: "image")
      - content.type: "image"
      - content.data: 이미지 관련 정보
   
   C. 차트 (format: "chart")
      - content.type: "chart"
      - content.data: {
          "type": "bar" | "pie" | "line" | "scatter" | "radar",
          "chartData": {
            "labels": ["레이블1", "레이블2"],
            "values": [값1, 값2]
          },
          "title": "차트 제목"
        }

=== 타입별 세부 규칙 ===

**PPT 타입 (slideType: "ppt"):**
- 내용 객체: 2-6개
- 2개 생성 시: 텍스트는 목록 형태로 120자 내외
- 4개 생성 시: 60자 내외  
- 6개 생성 시: 30자 내외

**카드뉴스 타입 (slideType: "card-news"):**
- 내용 객체: 1-4개
- 1개 생성 시: 텍스트 120자 내외
- 2개 생성 시: 60자 내외
- 4개 생성 시: 30자 내외

=== 슬라이드 구성 규칙 ===

1. **제목 배치**: 화면 상단 15% 영역, 가로/세로 최대 80%
2. **내용 배치**: 나머지 85% 영역에 동적 레이아웃 적용
3. **테마 색상**: 모든 요소의 배경색에 테마 색상 적용
4. **반응형 글씨**: 박스 크기에 맞춰 자동 조절

=== 이모지 가이드 ===
- 각 텍스트 항목의 머릿말에 적절한 이모지 사용
- 추천 이모지: 💡📌✨🎯🔸🔹✅⚡📝🚀📊💬📋🔍🎨🔔🌟⭐🌐💎🔥❤️💪🎉📈

내용: ${content}

응답은 반드시 유효한 JSON 배열 형식으로만 작성해주세요:

[
  {
    "slideNumber": 1,
    "category": "title",
    "format": "short-text", 
    "designStyle": "title-signboard",
    "content": "슬라이드 제목",
    "metadata": {
      "backgroundColor": "theme"
    }
  },
  {
    "slideNumber": 1,
    "category": "content",
    "format": "text-list",
    "designStyle": "content-list",
    "content": {
      "items": [
        {
          "designElement": "네모 div",
          "emoji": "⭐",
          "heading": "머릿말",
          "description": "설명 내용..."
        }
      ]
    },
    "metadata": {
      "backgroundColor": "theme",
      "columns": 1
    }
  }
]

중요 요구사항: 
- 반드시 정확히 ${slideCount}개의 슬라이드 생성 (slideNumber: 1, 2, 3, ..., ${slideCount})
- 각 슬라이드마다 제목 객체(category: "title")와 내용 객체(category: "content") 모두 포함
- 타입별 객체 개수 및 글자 수 규칙 준수
- 제목은 15% 영역, 내용은 85% 영역에 배치
- 모든 요소에 테마 색상 적용

응답 구조 예시 (${slideCount}개 슬라이드의 경우):
- 슬라이드 1: slideNumber=1인 title 객체 1개 + content 객체 ${typeRules.objectCount}
- 슬라이드 2: slideNumber=2인 title 객체 1개 + content 객체 ${typeRules.objectCount}
- ...
- 슬라이드 ${slideCount}: slideNumber=${slideCount}인 title 객체 1개 + content 객체 ${typeRules.objectCount}
`;
};

/**
 * @deprecated
 * 이 함수는 더 이상 사용되지 않습니다.
 * createSlideUpdatePrompt를 사용해주세요.
 * 슬라이드 업데이트 프롬프트는 slidePrompts.ts에서 관리됩니다.
 */
export const createSlideUpdatePrompt = (
  currentTitle: string,
  currentContent: string,
  updateRequest: string,
  language: 'ko' | 'en'
): string => {
  console.warn('createSlideUpdatePrompt is deprecated. Use createSlideUpdatePrompt from slidePrompts.ts');
  
  // 호환성을 위해 slidePrompts.ts로 리다이렉트
  return newCreateSlideUpdatePrompt(currentTitle, currentContent, updateRequest, language);
};

export const AI_CONTENT_PROMPTS = {
  ko: [
    // 기술 분야 (통계/데이터 포함)
    "최신 AI 기술 동향과 미래 전망에 대한 발표 자료를 만들어주세요. AI 시장 규모 성장률, 산업별 AI 도입률, 주요 기술별 투자 비중 등의 통계 데이터를 포함해주세요.",
    "블록체인 기술의 원리와 실생활 적용 사례를 소개해주세요. 암호화폐 시장 점유율, 산업별 블록체인 활용 현황, 거래 처리 속도 비교 차트를 포함해주세요.",
    "사물인터넷(IoT)의 발전과 스마트 시티 구축 방안을 설명해주세요. IoT 기기 보급률 추이, 스마트시티 투자 규모, 분야별 IoT 활용 비율을 차트로 보여주세요.",
    "클라우드 컴퓨팅의 장점과 기업 도입 전략을 다뤄주세요. 클라우드 서비스 시장 점유율, 도입 전후 비용 절감 효과, 보안 위협 유형별 비율을 시각화해주세요.",
    "빅데이터 분석의 중요성과 활용 방법을 제시해주세요. 데이터 증가율 추이, 산업별 빅데이터 활용도, 분석 기법별 효과성 비교를 차트로 포함해주세요.",
    
    // 비즈니스 분야 (성과 지표/ROI 포함)
    "창업을 위한 비즈니스 모델 구성과 투자 유치 방법을 소개해주세요. 스타트업 생존율 통계, 투자 단계별 평균 금액, 산업별 투자 비중을 그래프로 보여주세요.",
    "디지털 마케팅 전략과 소셜미디어 활용법에 대한 프레젠테이션을 준비해주세요. 채널별 전환율, 연령대별 SNS 이용률, 마케팅 예산 배분 비율을 차트로 포함해주세요.",
    "고객 경험(CX) 향상을 위한 서비스 디자인 방법론을 설명해주세요. 고객 만족도 개선 효과, 서비스 접점별 중요도, CX 투자 대비 수익률을 데이터로 제시해주세요.",
    "원격 근무 시대의 팀 관리와 생산성 향상 방안을 다뤄주세요. 원격근무 도입률 변화, 생산성 지표 비교, 협업 도구 사용률 통계를 시각화해주세요.",
    "ESG 경영의 중요성과 기업의 지속가능성 전략을 소개해주세요. ESG 평가 등급별 기업 분포, 지속가능 투자 규모 성장률, ESG 실천 전후 기업가치 변화를 차트로 포함해주세요.",
    
    // 교육 분야 (학습 효과/참여율 데이터 포함)
    "온라인 교육의 효과적인 설계와 운영 방안에 대해 논의해주세요. 온라인 vs 오프라인 학습 효과 비교, 학습 플랫폼별 이용률, 연령대별 선호 학습 방식을 그래프로 보여주세요.",
    "메타버스를 활용한 몰입형 학습 환경 구축 방법을 설명해주세요. 메타버스 교육 플랫폼 시장 규모, 학습 몰입도 개선 효과, 분야별 메타버스 교육 활용률을 시각화해주세요.",
    "개인 맞춤형 학습 시스템과 적응형 교육 기술을 다뤄주세요. 맞춤형 학습 도입 전후 성취도 비교, AI 튜터 효과성, 학습 패턴 분석 결과를 차트로 포함해주세요.",
    "디지털 리터러시 교육의 필요성과 실천 방안을 제시해주세요. 연령별 디지털 리터러시 수준, 국가별 디지털 교육 투자액, 디지털 역량과 취업률 상관관계를 데이터로 보여주세요.",
    
    // 환경/사회 분야 (환경 지표/효과 측정 포함)
    "지속가능한 환경을 위한 친환경 기술과 실천 방안에 대해 설명해주세요. 탄소 배출량 감소 추이, 친환경 기술별 효과성, 국가별 환경 정책 투자 비중을 차트로 시각화해주세요.",
    "재생에너지의 종류와 효율적인 활용 방법을 소개해주세요. 에너지원별 발전 비중 변화, 재생에너지 효율성 비교, 지역별 재생에너지 보급률을 그래프로 포함해주세요.",
    "순환경제 시스템 구축과 폐기물 관리 혁신을 다뤄주세요. 재활용률 추이, 폐기물 처리 방식별 비용, 순환경제 도입 효과를 데이터로 제시해주세요.",
    "도시농업과 스마트팜 기술의 발전 방향을 설명해주세요. 스마트팜 시장 성장률, 작물별 생산성 향상 효과, 도시농업 참여율 증가 추이를 차트로 보여주세요.",
    
    // 건강/라이프스타일 분야 (건강 지표/통계 포함)
    "건강한 라이프스타일을 위한 운동과 영양 관리법을 제시해주세요. 운동 빈도별 건강 개선 효과, 영양소별 권장 섭취량, 연령대별 운동 참여율을 그래프로 시각화해주세요.",
    "정신건강 관리와 스트레스 해소 방법을 소개해주세요. 스트레스 원인별 비중, 명상/운동의 스트레스 감소 효과, 정신건강 서비스 이용률 추이를 데이터로 포함해주세요.",
    "디지털 헬스케어 기술과 개인 건강 관리 도구를 다뤄주세요. 웨어러블 기기 시장 성장률, 헬스케어 앱 사용자 만족도, 원격의료 이용 증가율을 차트로 보여주세요.",
    "일과 삶의 균형을 위한 시간 관리 기법을 설명해주세요. 업무 시간 대비 생산성, 워라밸 만족도와 이직률 상관관계, 시간 관리 도구 사용 효과를 시각화해주세요.",
    
    // 글로벌/문화 분야 (시장 규모/문화 지표 포함)
    "글로벌 시장 진출을 위한 국제화 전략과 문화적 고려사항을 다뤄주세요. 지역별 시장 성장률, 문화 차이로 인한 실패 사례 비율, 현지화 전략별 성공률을 데이터로 제시해주세요.",
    "다문화 사회의 이해와 상호 존중 문화 조성 방안을 제시해주세요. 국가별 다문화 인구 비율, 문화 다양성 지수, 다문화 정책 예산 추이를 차트로 포함해주세요.",
    "K-컬처의 세계적 확산과 문화 콘텐츠 산업 전망을 소개해주세요. K-콘텐츠 수출액 성장률, 국가별 한류 콘텐츠 소비 비중, 장르별 인기도를 그래프로 시각화해주세요.",
    
    // 미래/트렌드 분야 (예측 데이터/트렌드 분석 포함)
    "미래 직업 트렌드와 필요한 역량 개발 방향에 대해 분석해주세요. 직업별 고용 전망, 미래 핵심 역량 중요도, 산업별 일자리 변화 예측을 차트로 보여주세요.",
    "인구 고령화 사회의 도전과 기회를 다뤄주세요. 연령별 인구 구조 변화, 고령화 관련 산업 성장률, 세대별 경제활동 참가율을 데이터로 시각화해주세요.",
    
    // 데이터/차트 분야 (새로 추가)
    "최근 5년간 전자상거래 시장 성장률을 막대 그래프로 보여주세요",
    "전 세계 재생에너지 사용 비중을 파이 차트로 시각화해주세요",
    "코로나19 이후 재택근무 비율 변화를 라인 차트로 표현해주세요",
    "스마트폰 OS별 시장 점유율을 도넛 차트로 분석해주세요",
    "연령대별 소셜미디어 사용 시간을 히트맵으로 보여주세요",
    "국가별 AI 투자 규모를 트리맵으로 시각화해주세요",
    "기업 부서별 예산 배분을 산키 다이어그램으로 표현해주세요",
    "주요 도시별 평균 기온 변화를 레이더 차트로 보여주세요",
    "가격과 품질의 상관관계를 산점도로 분석해주세요",
    "분기별 매출 추이와 목표 달성률을 복합 차트로 표현해주세요",
    "제품별 고객 만족도 평가를 레이더 차트로 시각화해주세요",
    "시간대별 웹사이트 트래픽을 히트맵으로 분석해주세요",
    "프로젝트별 리소스 할당을 트리맵으로 보여주세요",
    "마케팅 채널별 ROI를 막대 그래프로 비교해주세요",
    "고객 세그먼트별 구매 패턴을 산점도로 표현해주세요",
    "팀별 역량 평가를 레이더 차트로 시각화해주세요",
    
    // 추가 비즈니스 분야
    "프로젝트 관리 방법론(애자일 vs 워터폴)의 장단점을 비교해주세요",
    "스타트업 성장 단계별 필요한 전략과 과제를 설명해주세요",
    "B2B 영업 프로세스 최적화 방안을 제시해주세요",
    "브랜드 아이덴티티 구축과 리브랜딩 전략을 소개해주세요",
    
    // 추가 기술 분야
    "양자컴퓨팅의 원리와 미래 응용 분야를 설명해주세요",
    "메타버스 플랫폼의 종류와 비즈니스 활용 사례를 다뤄주세요",
    "사이버보안 위협의 유형과 대응 방안을 제시해주세요",
    "엣지 컴퓨팅의 개념과 5G 시대의 활용 방안을 설명해주세요",
    
    // 추가 교육/자기계발 분야
    "효과적인 프레젠테이션 스킬과 청중 소통 방법을 가르쳐주세요",
    "창의적 문제 해결을 위한 디자인 씽킹 프로세스를 소개해주세요",
    "개인 브랜딩 전략과 네트워킹 기법을 설명해주세요",
    "리더십 유형별 특징과 상황별 적용 방법을 다뤄주세요"
  ],
  en: [
    // Technology
    "Create a presentation about the latest AI technology trends and future prospects",
    "Introduce blockchain technology principles and real-life application cases",
    "Explain IoT development and smart city construction strategies",
    "Cover cloud computing advantages and enterprise adoption strategies",
    "Present the importance and utilization methods of big data analysis",
    
    // Business
    "Introduce business model composition and investment attraction methods for startups",
    "Prepare a presentation on digital marketing strategies and social media utilization",
    "Explain service design methodologies for improving customer experience (CX)",
    "Cover team management and productivity improvement in the remote work era",
    "Introduce ESG management importance and corporate sustainability strategies",
    
    // Education
    "Discuss effective design and operation of online education",
    "Explain methods for building immersive learning environments using metaverse",
    "Cover personalized learning systems and adaptive education technologies",
    "Present the necessity and practical plans for digital literacy education",
    
    // Environment/Society
    "Explain eco-friendly technologies and practices for sustainable environment",
    "Introduce types of renewable energy and efficient utilization methods",
    "Cover circular economy system construction and waste management innovation",
    "Explain the development direction of urban agriculture and smart farm technology",
    
    // Health/Lifestyle
    "Present exercise and nutrition management for a healthy lifestyle",
    "Introduce mental health management and stress relief methods",
    "Cover digital healthcare technology and personal health management tools",
    "Explain time management techniques for work-life balance",
    
    // Global/Culture
    "Cover internationalization strategies and cultural considerations for global market entry",
    "Present understanding of multicultural society and mutual respect culture creation",
    "Introduce K-culture's global expansion and cultural content industry prospects",
    
    // Future/Trends
    "Analyze future job trends and necessary competency development directions",
    "Cover challenges and opportunities in an aging society",
    
    // Data/Charts (Newly added)
    "Show e-commerce market growth rate for the past 5 years in a bar chart",
    "Visualize global renewable energy usage share with a pie chart",
    "Express remote work ratio changes after COVID-19 with a line chart",
    "Analyze smartphone OS market share with a donut chart",
    "Show social media usage time by age group with a heatmap",
    "Visualize AI investment scale by country with a treemap",
    "Express department budget allocation with a Sankey diagram",
    "Show average temperature changes by major cities with a radar chart",
    "Analyze price-quality correlation with a scatter plot",
    "Express quarterly sales trends and target achievement with a combo chart",
    "Visualize product satisfaction ratings with a radar chart",
    "Analyze website traffic by time of day with a heatmap",
    "Show project resource allocation with a treemap",
    "Compare marketing channel ROI with a bar chart",
    "Express customer segment purchase patterns with a scatter plot",
    "Visualize team competency assessment with a radar chart",
    
    // Additional Business
    "Compare pros and cons of project management methodologies (Agile vs Waterfall)",
    "Explain strategies and challenges for each startup growth stage",
    "Present B2B sales process optimization strategies",
    "Introduce brand identity building and rebranding strategies",
    
    // Additional Technology
    "Explain quantum computing principles and future applications",
    "Cover types of metaverse platforms and business use cases",
    "Present types of cybersecurity threats and countermeasures",
    "Explain edge computing concepts and 5G era applications",
    
    // Additional Education/Self-development
    "Teach effective presentation skills and audience communication methods",
    "Introduce design thinking process for creative problem solving",
    "Explain personal branding strategies and networking techniques",
    "Cover leadership types and situational application methods"
  ]
} as const;